<script setup lang="ts">
import { EEventActions } from '@/store/eventStore/EventStoreTypes'
import { EStoreModules } from '@/store/storeType'
import type { EventModel } from '@/types/models/EventModel'
import {
    BaseDialog,
    CreateButton,
    EditableDateTimeField,
    InformationBaseCard,
    PermissionWrapper,
    StackLayout
} from '@rfc/rfc-components-library'
import type { ExtendedPrimitives } from '@rfc/rfc-components-library/types'
import { isEqual } from 'lodash'
import moment from 'moment'
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import PartnersSelector from '../../../../components/Selector/PartnersSelector.vue'
import type { ConfigType, PartnerConfigModel } from '../../../../types/models/PartnerConfigModel'
import type { PartnerModel } from '../../../../types/partners/PartnerModel'
import { EPartnersPerm } from '../../../../types/permissions/PartnersPermissionsEnum'
import EventDetailInformations from './EventDetailInformations.vue'
import EventPartnerConfig from './EventPartnerConfig.vue'

const store = useStore()

type Props = {
    eventId: number
    loading?: boolean

    data: EventModel
    partnerConfigData?: PartnerModel
    partnerConfig?: Record<string, ConfigType | string>
    partnerConfigValues?: Record<string, ExtendedPrimitives>
}
const props = defineProps<Props>()

type Emits = {
    (e: 'update', value: Partial<EventModel>): void
    (e: 'refresh'): void
    (e: 'selectPartner', value: Partial<EventModel>): void
    (e: 'updatePartnerConfig', value: Partial<PartnerConfigModel>): void
}
const emit = defineEmits<Emits>()

const dataEvent = ref<EventModel>({ ...props.data })
const open = ref(false)

const storedEvent = computed(() => store?.state.event?.data)

const handleOpen = () => {
    open.value = true
    dataEvent.value = { ...props.data }
    editMode.value = true
}

const save = () => {
    if (!isEqual(dataEvent.value, storedEvent.value)) {
        store
            ?.dispatch(`${EStoreModules.EVENT}/${EEventActions.UPDATE_EVENT}`, {
                eventId: props.eventId,
                data: dataEvent.value
            })
            .then(() => {
                emit('refresh')
                open.value = false
            })
    } else open.value = false
}

const handleUpdatePartner = (partner: Partial<EventModel>) =>
    (dataEvent.value.partner_config = { ...dataEvent.value.partner_config, ...partner })

const handleSelectPartner = (value: Partial<EventModel>) => {
    dataEvent.value?.partner?.id &&
    value.partner?.id &&
    dataEvent.value?.partner?.id === value.partner?.id
        ? undefined
        : (dataEvent.value = { ...dataEvent.value, ...value })
}

const editMode = ref(false)
</script>

<template>
    <CreateButton
        :loading="loading"
        :label="$t('event.button.editEvent')"
        @click="handleOpen"
        class="anim-btn" />

    <BaseDialog
        :open="open"
        @confirm="save"
        @close="open = false"
        class="md:w-2/3 lg:w-1/2"
        :title="$t('event.detail.informations.editTitle')">
        <template #content>
            <StackLayout direction="column" :gap="16" class="pt-5 w-full">
                <EventDetailInformations
                    class="w-full"
                    :data="dataEvent"
                    :editMode="editMode"
                    @change="dataEvent = { ...dataEvent, ...$event }" />
                <InformationBaseCard :label="$t('event.title.datesTitle')" class="w-full">
                    <StackLayout class="w-full" :gap="16" isResponsiveSmallScreen>
                        <EditableDateTimeField
                            :label="$t('event.model.startAt')"
                            :modelValue="moment(dataEvent?.start_at).toDate()"
                            editMode
                            boldLabel
                            required
                            @change="dataEvent.start_at = $event"
                            class="w-full" />
                        <EditableDateTimeField
                            :label="$t('event.model.endAt')"
                            :modelValue="moment(dataEvent?.end_at).toDate()"
                            editMode
                            boldLabel
                            required
                            @change="dataEvent.end_at = $event"
                            class="w-full" />
                    </StackLayout>
                </InformationBaseCard>
                <InformationBaseCard
                    :label="$t('event.detail.informations.partnerConfigUpdate')"
                    class="w-full">
                    <StackLayout direction="column" :gap="16" class="w-full">
                        <PermissionWrapper :permission="EPartnersPerm.PARTNERS_VIEW">
                            <PartnersSelector
                                :partner_config="dataEvent.partner_config"
                                :partnerId="dataEvent.partner_id"
                                :currentPartner="dataEvent.partner"
                                @update="handleSelectPartner"
                                class="w-full" />
                        </PermissionWrapper>
                        <EventPartnerConfig
                            v-if="
                                (partnerConfig || partnerConfigValues) &&
                                dataEvent.partner_id === dataEvent.partner?.id
                            "
                            :partnerConfigData="partnerConfigData"
                            :partnerConfig="partnerConfig"
                            :partnerConfigValues="partnerConfigValues"
                            editMode
                            @update="handleUpdatePartner" />
                    </StackLayout>
                </InformationBaseCard>
            </StackLayout>
        </template>
    </BaseDialog>
</template>
